import type { Nullable } from "@core/domain/types/nullable.type";
import type { KanbanPendingByAssigneeBucket } from "@project/domain/models/kanban-reports.model";

type ActivityResponsibleSource = {
    employeeIds?: number[];
    assignedEmployee?: number | null | undefined;
};

type ReportResponsibleSource = {
    teamEmployeeIds?: number[];
    assignedEmployeeId?: Nullable<number>;
};

export const getActivityResponsibleEmployeeIds = (
    activity: ActivityResponsibleSource,
): number[] => {
    const ids = new Set(activity.employeeIds ?? []);

    if (activity.assignedEmployee) {
        ids.add(activity.assignedEmployee);
    }

    return Array.from(ids);
};

export const getReportResponsibleEmployeeIds = (
    item: ReportResponsibleSource,
): number[] => {
    const ids = new Set(item.teamEmployeeIds ?? []);

    if (item.assignedEmployeeId) {
        ids.add(item.assignedEmployeeId);
    }

    return Array.from(ids);
};

export const groupReportItemsByResponsible = <
    T extends ReportResponsibleSource,
>(
    items: T[],
): Array<{
    responsibleEmployeeId: Nullable<number>;
    count: number;
    activities: T[];
}> => {
    const buckets = new Map<Nullable<number>, T[]>();

    for (const item of items) {
        const responsibleIds = getReportResponsibleEmployeeIds(item);
        const keys: Array<Nullable<number>> = responsibleIds.length
            ? responsibleIds
            : [null];

        for (const key of keys) {
            const bucket = buckets.get(key) ?? [];
            bucket.push(item);
            buckets.set(key, bucket);
        }
    }

    return Array.from(buckets.entries())
        .map(([responsibleEmployeeId, activities]) => ({
            responsibleEmployeeId,
            count: activities.length,
            activities,
        }))
        .sort((left, right) => {
            if (left.responsibleEmployeeId === null) return 1;
            if (right.responsibleEmployeeId === null) return -1;

            return left.responsibleEmployeeId - right.responsibleEmployeeId;
        });
};

export const toAssigneeBuckets = <T extends ReportResponsibleSource>(
    items: T[],
): KanbanPendingByAssigneeBucket[] =>
    groupReportItemsByResponsible(items).map((bucket) => ({
        assignedEmployeeId: bucket.responsibleEmployeeId,
        count: bucket.count,
        activities:
            bucket.activities as unknown as KanbanPendingByAssigneeBucket["activities"],
    }));

export const activityMatchesResponsibleFilter = (
    activity: ActivityResponsibleSource,
    filterResponsibleEmployeeIds: number[],
): boolean => {
    if (!filterResponsibleEmployeeIds.length) {
        return true;
    }

    const responsibleIds = getActivityResponsibleEmployeeIds(activity);

    return filterResponsibleEmployeeIds.some((employeeId) =>
        responsibleIds.includes(employeeId),
    );
};
